import { scheduleMapper, scheduleItemMapper, holidayExceptionDateMapper } from "./mappers";
import { MSSub } from "../subscription";
import { HolidayExceptionDate, Schedule, ScheduleInfo, ScheduleItem } from "@lib/types";
import { HolidayExceptionDateDto, ScheduleDto, ScheduleInfoDto, ScheduleItemDto } from "./dtos";

const GET_SCHEDULES_PATH = "/Methods/GetSchedules";
const GET_SCHEDULE_INFO_PATH = "/Methods/GetScheduleInfo";
const GET_SCHEDULE_TASKS_PATH = "/Methods/GetTasks";
const ADD_TASK_PATH = "/Methods/AddTask";
const DELETE_TASK_PATH = "/Methods/DeleteTask";
const UPDATE_TASK_PATH = "/Methods/UpdateTask";
const SET_SCHEDULE_STATE_PATH = "/Methods/SetScheduleState";
const ADD_EXCEPTION_DATES_PATH = "/Methods/AddExceptionDates";
const GET_EXCEPTION_DATES_PATH = "/Methods/GetExceptionDates";

type GetScheduleTasksOptions = Readonly<{
    id: Schedule["scheduleId"];
    path: Schedule["path"];
}>;

type AddScheduleItemOptions = Readonly<{
    item: ScheduleItem;
    scheduleId: Schedule["scheduleId"];
    path: Schedule["path"];
    fullPath: Schedule["fullPath"];
}>;

type GetTasksResult = Readonly<{
    tasks: readonly ScheduleItemDto[];
}>;

type AddScheduleItemResult = Readonly<{
    status: "success" | "error";
    message: string;
    taskId: number;
}>;

type SetScheduleStateOptions = Readonly<{
    scheduleId: Schedule["scheduleId"];
    path: Schedule["path"];
    enabled: boolean;
    locked: boolean;
}>;

type GetExceptionDatesResponse = Readonly<{
    exceptionDates: HolidayExceptionDateDto[];
}>;

/** Schedule API. */
export class MsScheduleApi extends MSSub {

    /**
     * GEts schedules.
     * @param itemId Item ID.
     * @param path Path.
     */
    public async getSchedules(itemId: number, path: string): Promise<Schedule[]> {
        try {
            const res = await this.req.post<{ schedules: ScheduleDto[] }>(GET_SCHEDULES_PATH, {
                sessionId: this.sessionId,
                itemId,
                path,
            });
            return res.data.schedules.map(dto => scheduleMapper.fromDto(dto));
        } catch(e) {
            console.error("Error in getSchedules ", e);
            return [];
        }
    }

    /**
     * Gets schedule info.
     * @param scheduleId Schedule ID.
     * @param path Schedule path.
     */
    public async getScheduleInfo(scheduleId: Schedule["scheduleId"], path: Schedule["path"]): Promise<ScheduleInfo> {
        try {
            const response = await this.req.post<ScheduleInfoDto>(GET_SCHEDULE_INFO_PATH, {
                sessionId: this.sessionId,
                scheduleId,
                path,
            });

            return scheduleMapper.fromScheduleInfoDto(response.data);
        } catch(e) {
            console.error("Error in getScheduleInfo", e);
            return {
                scheduleId: -1,
                path: "",
                isEnabledFb: false,
                isEnabledHmi: false,
                isLocked: false,
            };
        }
    }

    /**
     * Gets schedule tasks.
     * @param options Options.
     */
    public async getScheduleTasks({ id, path }: GetScheduleTasksOptions): Promise<ScheduleItem[]> {
        try {
            const result = await this.req.post<GetTasksResult>(GET_SCHEDULE_TASKS_PATH, {
                sessionId: this.sessionId,
                scheduleId: id,
                path,
            });
            return result.data.tasks.map(dto => scheduleItemMapper.fromDto(dto));
        } catch(e) {
            console.error(e);
            return [];
        }
    }

    /**
     * Adds new schedule item.
     * @param options Options.
     */
    public async addScheduleItem(options: AddScheduleItemOptions): Promise<number> {
        try {
            const result = await this.req.post<AddScheduleItemResult>(ADD_TASK_PATH, {
                sessionId: this.sessionId,
                scheduleId: options.scheduleId,
                schedulePath: options.path,
                scheduleFullPath: options.fullPath,
                ...scheduleItemMapper.toDto(options.item),
            });

            if (result.data.status !== "success") {
                throw new Error(result.data.message);
            }

            return result.data.taskId;
        } catch(e: unknown) {
            console.error("Error in addScheduleItem ", e);
            return -1;
        }
    }

    /**
     * Removes task by ID.
     * @param taskId Task ID.
     */
    public async removeTaskById(taskId: ScheduleItem["id"]): Promise<void> {
        try {
            await this.req.post(DELETE_TASK_PATH, {
                sessionId: this.sessionId,
                taskId,
            });
        } catch(e) {
            console.error("Error in removeTaskById", e);
        }
    }

    /**
     * Updates task.
     * @param item Schedule item.
     */
    public async updateTask(item: ScheduleItem): Promise<void> {
        try {
            await this.req.post(UPDATE_TASK_PATH, {
                sessionId: this.sessionId,
                ...scheduleItemMapper.toUpdateDto(item),
            });
        } catch(e) {
            console.error("Error in updateTask", e);
        }
    }

    /**
     * Updates schedule state.
     * @param options Options.
     */
    public async updateScheduleState(options: SetScheduleStateOptions): Promise<void> {
        try {
            await this.req.post(SET_SCHEDULE_STATE_PATH, {
                sessionId: this.sessionId,
                scheduleId: options.scheduleId,
                path: options.path,
                enabled: options.enabled,
                locked: options.locked,
            });
        } catch(e) {
            console.error("Error in updateScheduleState", e);
        }
    }

    /**
     * Sets new holiday exception dates.
     * @param exceptionDates Holiday exception dates.
     */
    public async setExceptionDates(exceptionDates: readonly HolidayExceptionDate[]): Promise<void> {
        try {
            await this.req.post(ADD_EXCEPTION_DATES_PATH, {
                sessionId: this.sessionId,
                exceptionDates: exceptionDates.map(model => holidayExceptionDateMapper.toDto(model)),
            });
        } catch(e) {
            console.error("Error in setExceptionDates", e);
        }
    }

    /** Gets holiday exception dates. */
    public async getExceptionDates(): Promise<HolidayExceptionDate[]> {
        try {
            const response = await this.req.post<GetExceptionDatesResponse>(GET_EXCEPTION_DATES_PATH, {
                sessionId: this.sessionId,
            });
            return response.data?.exceptionDates?.map(dto => holidayExceptionDateMapper.fromDto(dto)) ?? [];
        } catch(e) {
            console.error("Error in getExceptionDates", e);
            return [];
        }
    }
}
